import { useState, useRef, useCallback } from 'react';
import {
  initAttachmentUpload,
  uploadToSignedUrl,
  finalizeAttachmentUpload,
} from '@/api/attachments';
import { showError } from '@/lib/toast';

export type UploadState = 'idle' | 'init' | 'uploading' | 'done' | 'error';

const MAX_RETRIES = 2;
const RETRY_DELAY_BASE = 1000; // 1 second

interface UploadParams {
  file: File;
  messageId: string;
}

interface UploadResult {
  attachmentId: string;
}

/**
 * Hook for managing attachment upload with automatic retry
 *
 * Features:
 * - Automatic retry (up to 2 attempts) on upload failure
 * - Manual retry via retry() function
 * - Progress tracking
 * - Error state management
 *
 * @param conversationId - The conversation to upload to
 */
export function useAttachmentUpload(conversationId: string) {
  const [state, setState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Store last upload params for manual retry
  const lastUploadRef = useRef<UploadParams | null>(null);

  const upload = useCallback(
    async (file: File, messageId: string, isRetry = false): Promise<UploadResult> => {
      lastUploadRef.current = { file, messageId };
      if (!isRetry) {
        setRetryCount(0);
      }

      try {
        setState('init');
        setProgress(0);
        setError(null);

        // Step 1: Get signed upload URL
        const initRes = await initAttachmentUpload({
          conversation_id: conversationId,
          message_id: messageId,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        });

        setState('uploading');

        // Step 2: Upload with automatic retry
        let lastError: Error | null = null;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            await uploadToSignedUrl(
              initRes.storage_path,
              initRes.upload_token,
              file,
              setProgress
            );

            // Step 3: Finalize upload (confirms file exists in storage)
            await finalizeAttachmentUpload({
              attachment_id: initRes.attachment_id,
            });

            setState('done');
            return { attachmentId: initRes.attachment_id };
          } catch (err) {
            lastError = err instanceof Error ? err : new Error('Upload failed');

            if (attempt < MAX_RETRIES) {
              setRetryCount(attempt + 1);
              // Wait with exponential backoff before retry
              await new Promise((r) =>
                setTimeout(r, RETRY_DELAY_BASE * (attempt + 1))
              );
              setProgress(0); // Reset progress for retry
            }
          }
        }

        // All automatic retries failed
        throw lastError;
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Upload failed');
        setState('error');
        setError(uploadError);
        showError('Failed to upload attachment. You can retry from the message.');
        throw uploadError;
      }
    },
    [conversationId]
  );

  // Manual retry function (for retry button)
  const retry = useCallback(async (): Promise<UploadResult | undefined> => {
    if (!lastUploadRef.current) return undefined;
    const { file, messageId } = lastUploadRef.current;
    return upload(file, messageId, true);
  }, [upload]);

  const reset = useCallback(() => {
    setState('idle');
    setProgress(0);
    setError(null);
    setRetryCount(0);
    lastUploadRef.current = null;
  }, []);

  return {
    upload,
    retry,
    state,
    progress,
    error,
    retryCount,
    reset,
  };
}
